import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { map, Observable, of } from 'rxjs';

import { ENVIRONMENT, Environment } from '@hmax-order/frontend/environment';
import { Option, Role, User } from '@hmax-order/shared/types';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _env = inject<Environment>(ENVIRONMENT);
  private _http = inject(HttpClient);

  private _apiUrl: string;
  private _apiUrlUsers: string;
  private _apiUrlRoles: string;

  constructor() {
    this._apiUrl = `${this._env.apiUrl}`;
    this._apiUrlUsers = `${this._apiUrl}/users`;
    this._apiUrlRoles = `${this._apiUrl}/roles`;
  }

  findAll(): Observable<User[]> {
    return this._http.get<User[]>(`${this._apiUrlUsers}`);
  }

  findAllSummarized(): Observable<User[]> {
    return this._http.get<User[]>(`${this._apiUrlUsers}/summarized`);
  }

  getById(id: string): Observable<User> {
    return this._http.get<User>(`${this._apiUrlUsers}/${id}`);
  }

  /**
   * Buscar papéis disponíveis no sistema
   */
  getRoles(): Observable<Role[]> {
    return this._http.get<Role[]>(`${this._apiUrlRoles}`);
  }

  create(user: User): Observable<User[]> {
    return this._http.post<User[]>(this._apiUrlUsers, user);
  }

  update(id: string, user: User): Observable<User[]> {
    return this._http.patch<User[]>(`${this._apiUrlUsers}/${id}`, user);
  }

  remove(id: string): Observable<User> {
    return this._http.delete<User>(`${this._apiUrlUsers}/${id}`);
  }

  /**
   * Transformar formulário em usuário
   * @param userForm Formulário para cadastro/alteração do usuário
   */
  formToUser(userForm: any, userId?: string): Partial<User> {
    const { name, email, password, confirmPassword, roles, active, accessScheduleEnabled, accessStartTime, accessEndTime, accessWeekdays } =
      userForm;
    const newRoles = roles.map((role: Role) => role?.name);
    const scheduleEnabled = !!accessScheduleEnabled;

    const user = {
      name: name?.trim() ?? null,
      roles: newRoles,
      active: !userId ? true : active,
      email: email?.trim() ?? null,
      // Restrição de acesso por faixa de horário. Quando desabilitada, limpa os horários/dias.
      accessScheduleEnabled: scheduleEnabled,
      accessStartTime: scheduleEnabled ? accessStartTime || null : null,
      accessEndTime: scheduleEnabled ? accessEndTime || null : null,
      accessWeekdays: scheduleEnabled ? (accessWeekdays ?? []) : [],
    } as User;

    if (password != null && password === confirmPassword) {
      user.password = password;
    }

    return user;
  }

  /**
   * Buscar usuários como opções para selecionar
   * @param onlyId Deve retornar apenas ID como valor
   */
  getUsersAsAnOptions(onlyId = true): Observable<Option[]> {
    return this.findAll().pipe(
      map((users: User[]) => {
        return users.map((user: User) => {
          const value = onlyId ? user?.id : user;

          return {
            name: user?.name ?? '',
            value,
          } as Option;
        });
      }),
    );
  }

  syncUsersRo(): Observable<unknown> {
    return this._http.post(`${this._apiUrl}/ro/users/sync`, {});
  }

  getFromLocalStorage(): Observable<User> {
    // Obter dados do localStorage
    let stateLocal: any = localStorage.getItem('user');

    if (!stateLocal) {
      return of();
    }

    // Se houver dados do state no localStorage
    stateLocal = JSON.parse(stateLocal);
    return of(stateLocal);
  }
}
